import React, { useState } from 'react';
import Layout from '../components/Layout';
import { MapContainer, TileLayer, Marker, Polyline, Popup } from 'react-leaflet';
import { Search, Filter, X } from 'lucide-react';
import { useTrips } from '../hooks/useTrips';
import type { Trip } from '../lib/types';

const STATUS_LABELS: Record<Trip['status'], string> = {
  pending: 'Pending',
  active: 'Active',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<Trip['status'], string> = {
  pending: 'bg-slate-100 text-slate-600',
  active: 'bg-blue-50 text-blue-600',
  completed: 'bg-emerald-50 text-emerald-600',
  cancelled: 'bg-red-50 text-red-600',
};

function TripMapModal({ trip, onClose }: { trip: Trip; onClose: () => void }) {
  const hasRoute =
    trip.origin_lat != null && trip.origin_lng != null &&
    trip.dest_lat != null && trip.dest_lng != null;

  const origin: [number, number] | null = hasRoute ? [trip.origin_lat!, trip.origin_lng!] : null;
  const dest: [number, number] | null = hasRoute ? [trip.dest_lat!, trip.dest_lng!] : null;
  const center = origin ?? [59.3293, 18.0686];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
              Trip Route
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {trip.origin_address ?? 'Origin'} → {trip.dest_address ?? 'Destination'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="h-72">
          <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            {origin && (
              <Marker position={origin}>
                <Popup>Origin: {trip.origin_address}</Popup>
              </Marker>
            )}
            {dest && (
              <Marker position={dest}>
                <Popup>Destination: {trip.dest_address}</Popup>
              </Marker>
            )}
            {origin && dest && (
              <Polyline positions={[origin, dest]} color="#06B6D4" weight={4} />
            )}
          </MapContainer>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-4 bg-slate-50 border-t border-slate-100">
          {[
            { label: 'Fare', value: trip.fare != null ? `${trip.fare.toFixed(0)} kr` : '—' },
            { label: 'Distance', value: trip.distance_km != null ? `${trip.distance_km} km` : '—' },
            { label: 'App', value: trip.platform ?? 'Direkt' },
            { label: 'Payment', value: trip.payment_method ?? '—' },
            { label: 'Status', value: STATUS_LABELS[trip.status] },
            { label: 'Driver', value: (trip.driver as { full_name?: string } | undefined)?.full_name ?? '—' },
          ].map(({ label, value }) => (
            <div key={label}>
              <div className="text-xs text-slate-400">{label}</div>
              <div className="font-semibold text-slate-800 text-sm mt-0.5">{value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Trips: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<Trip['status'] | 'all'>('all');
  const [platformFilter, setPlatformFilter] = useState<'all' | 'direkt'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const perPage = 20;
  const { trips, total, loading } = useTrips({
    status: statusFilter,
    platform: platformFilter,
    dateFrom,
    dateTo,
    page,
    perPage,
  });
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const totalFare = trips.reduce((sum, t) => sum + (t.fare ?? 0), 0);

  const clearFilters = () => {
    setStatusFilter('all');
    setPlatformFilter('all');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return (
    <Layout
      title="Trips"
      breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Trips' }]}
    >
      {selectedTrip && (
        <TripMapModal trip={selectedTrip} onClose={() => setSelectedTrip(null)} />
      )}

      <div className="space-y-4">
        {/* Toolbar */}
        <div className="card px-5 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition ${showFilters ? 'bg-electric-50 border-electric-300 text-electric-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              <Filter className="w-4 h-4" /> Filters
            </button>
            {(statusFilter !== 'all' || platformFilter !== 'all' || dateFrom || dateTo) && (
              <button onClick={clearFilters} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition">
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>
          <div className="text-sm text-slate-500">
            <span className="font-medium text-slate-700">{total}</span> trips ·{' '}
            Total fare: <span className="font-semibold text-emerald-600">{totalFare.toFixed(0)} kr</span>
          </div>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div className="card px-5 py-4 flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value as Trip['status'] | 'all'); setPage(1); }}
                className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-electric-400"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Platform</label>
              <select
                value={platformFilter}
                onChange={(e) => { setPlatformFilter(e.target.value as 'all' | 'direkt'); setPage(1); }}
                className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-electric-400"
              >
                <option value="all">All apps</option>
                <option value="direkt">Direkt / Gatuhyra</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
              <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-electric-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
              <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-electric-400" />
            </div>
          </div>
        )}

        {/* Table */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full table-premium">
              <thead>
                <tr>
                  <th>Origin</th>
                  <th>Destination</th>
                  <th>Driver</th>
                  <th>App</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Fare</th>
                  <th>Distance</th>
                  <th>Date</th>
                  <th>Route</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? [...Array(10)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(10)].map((__, j) => (
                        <td key={j}><div className="skeleton h-4 rounded w-full" /></td>
                      ))}
                    </tr>
                  ))
                  : trips.length === 0
                    ? (
                      <tr>
                        <td colSpan={10} className="text-center py-12 text-slate-400">
                          <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
                          No trips found
                        </td>
                      </tr>
                    )
                    : trips.map((trip) => (
                      <tr key={trip.id}>
                        <td className="max-w-[140px] truncate text-slate-700">{trip.origin_address ?? '—'}</td>
                        <td className="max-w-[140px] truncate text-slate-700">{trip.dest_address ?? '—'}</td>
                        <td>{(trip.driver as { full_name?: string } | undefined)?.full_name ?? '—'}</td>
                        <td className="text-slate-600 text-sm">{trip.platform ?? 'Direkt'}</td>
                        <td className="text-slate-500 text-xs capitalize">{trip.payment_method ?? '—'}</td>
                        <td>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[trip.status]}`}>
                            {STATUS_LABELS[trip.status]}
                          </span>
                        </td>
                        <td className="font-semibold text-slate-800">
                          {trip.fare != null ? `${trip.fare.toFixed(0)} kr` : '—'}
                        </td>
                        <td>{trip.distance_km != null ? `${trip.distance_km} km` : '—'}</td>
                        <td className="text-slate-400 text-xs">
                          {new Date(trip.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <button
                            onClick={() => setSelectedTrip(trip)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-electric-600 hover:text-electric-800 hover:bg-electric-50 px-3 py-1.5 rounded-lg transition"
                          >
                            Map
                          </button>
                        </td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60">
            <div className="text-sm text-slate-500">
              Page <span className="font-medium text-slate-700">{page}</span> of{' '}
              <span className="font-medium text-slate-700">{totalPages}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                ← Prev
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Trips;
